import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  createRegulation,
  deleteRegulation,
  fetchRegulation,
  formatRegulationMarkdown,
  getApiErrorMessage,
  updateRegulation,
  uploadRegulationImage,
} from "../api";
import ErrorRetry from "../components/ErrorRetry";
import Loading from "../components/Loading";
import {
  REGULATION_CATEGORIES,
  categoryLabel,
  isRegulationCategory,
} from "../components/regulations/categories";
import MarkdownView from "../components/regulations/MarkdownView";
import {
  applyFormat,
  insertBlock,
  replaceSelection,
  selectionTouchesList,
} from "../components/regulations/markdownFormat";
import { pastedMarkdown } from "../components/regulations/pasteMarkdown";
import { useAuth } from "../context/AuthContext";
import { PERM } from "../permissions";

const DOC_DATE = new Intl.DateTimeFormat("ru-RU", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const EDITOR_PANES = [
  { id: "write", label: "Писать" },
  { id: "preview", label: "Как увидят" },
  { id: "split", label: "Рядом" },
];

const FORMAT_ACTIONS = [
  { id: "h2", label: "Заголовок" },
  { id: "h3", label: "Подзаголовок" },
  { id: "bold", label: "Жирный" },
  { id: "italic", label: "Курсив" },
  { id: "ul", label: "Маркеры" },
  { id: "ol", label: "Нумерация" },
  { id: "link", label: "Ссылка" },
];

function formatDocDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return DOC_DATE.format(date);
}

function imageSnippet(file, url) {
  const alt = String(file?.name || "скриншот").replace(/\.[^.]+$/, "");
  return `![${alt}](${url})`;
}

function defaultPane() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return "split";
  }
  return window.matchMedia("(min-width: 960px)").matches ? "split" : "write";
}

function keepToolbarFocus(event) {
  event.preventDefault();
}

export default function RegulationDoc() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canEdit = can(PERM.REGULATIONS_MANAGE);
  const isNew = id === "new";
  const startEditing = canEdit && (isNew || params.get("edit") === "1");
  const initialCategory = isRegulationCategory(params.get("category"))
    ? params.get("category")
    : "job";

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [editing, setEditing] = useState(startEditing);
  const [saving, setSaving] = useState(false);
  const [formatting, setFormatting] = useState(false);
  const [formatBackup, setFormatBackup] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [pane, setPane] = useState(defaultPane);
  const [dragOver, setDragOver] = useState(false);
  const [form, setForm] = useState({
    title: "",
    category: initialCategory,
    body: "",
  });
  const textareaRef = useRef(null);
  const titleRef = useRef(null);
  const fileRef = useRef(null);
  const docIdRef = useRef(isNew ? null : Number(id));
  const pendingSelection = useRef(null);

  const load = useCallback(async () => {
    if (isNew) return;
    const alreadyOpen = docIdRef.current === Number(id);
    if (!alreadyOpen) setLoading(true);
    setError("");
    try {
      const data = await fetchRegulation(id);
      setDoc(data);
      setForm({
        title: data.title,
        category: data.category,
        body: data.body || "",
      });
      docIdRef.current = data.id;
    } catch (err) {
      setError(getApiErrorMessage(err, "Не удалось открыть статью"));
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    load();
  }, [load]);

  const baseline = useMemo(
    () => ({
      title: doc?.title || "",
      category: doc?.category || initialCategory,
      body: doc?.body || "",
    }),
    [doc, initialCategory]
  );

  const dirty =
    editing &&
    (form.title !== baseline.title ||
      form.category !== baseline.category ||
      form.body !== baseline.body);

  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  useEffect(() => {
    if (!confirmDelete) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setConfirmDelete(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [confirmDelete]);

  useLayoutEffect(() => {
    const pending = pendingSelection.current;
    const area = textareaRef.current;
    if (!pending || !area) return;
    area.focus();
    area.setSelectionRange(pending.start, pending.end);
    pendingSelection.current = null;
  }, [form.body]);

  const applyToBody = useCallback((mutator) => {
    const area = textareaRef.current;
    setForm((prev) => {
      const start = area ? area.selectionStart : prev.body.length;
      const end = area ? area.selectionEnd : prev.body.length;
      const result = mutator(prev.body, start, end);
      pendingSelection.current = {
        start: result.selectionStart,
        end: result.selectionEnd,
      };
      return { ...prev, body: result.body };
    });
  }, []);

  const applyEditorFormat = useCallback(
    (action) => {
      if (pane === "preview") setPane("write");
      applyToBody((body, start, end) => applyFormat(body, start, end, action));
    },
    [applyToBody, pane]
  );

  const ensureDocument = useCallback(async () => {
    if (docIdRef.current) return docIdRef.current;
    const created = await createRegulation({
      title: form.title.trim() || "Новая инструкция",
      category: form.category,
      body: form.body,
    });
    docIdRef.current = created.id;
    setDoc(created);
    navigate(`/regulations/${created.id}?edit=1`, { replace: true });
    return created.id;
  }, [form.body, form.category, form.title, navigate]);

  const attachImage = useCallback(
    async (file) => {
      if (!file || !canEdit) return;
      const area = textareaRef.current;
      const start = area ? area.selectionStart : form.body.length;
      const end = area ? area.selectionEnd : form.body.length;
      setActionError("");
      setUploading(true);
      try {
        const documentId = await ensureDocument();
        const uploaded = await uploadRegulationImage(documentId, file);
        const snippet = imageSnippet(file, uploaded.url);
        applyToBody((body) => insertBlock(body, start, end, snippet));
      } catch (err) {
        setActionError(getApiErrorMessage(err, "Не удалось загрузить скриншот"));
      } finally {
        setUploading(false);
      }
    },
    [applyToBody, canEdit, ensureDocument, form.body.length]
  );

  const handlePaste = async (event) => {
    const clipboard = event.clipboardData;
    if (!clipboard) return;
    const items = clipboard.items ? Array.from(clipboard.items) : [];
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        event.preventDefault();
        const file = item.getAsFile();
        if (file) await attachImage(file);
        return;
      }
    }
    const html = typeof clipboard.getData === "function" ? clipboard.getData("text/html") : "";
    const text = typeof clipboard.getData === "function" ? clipboard.getData("text/plain") : "";
    const markdown = pastedMarkdown({ html, text });
    if (!markdown) return;
    event.preventDefault();
    applyToBody((body, start, end) => replaceSelection(body, start, end, markdown));
  };

  const handleFormat = async () => {
    if (!canEdit || formatting) return;
    setFormatting(true);
    setActionError("");
    try {
      const result = await formatRegulationMarkdown(form.body);
      const nextBody = result?.body ?? "";
      setFormatBackup(form.body);
      setForm((prev) => ({ ...prev, body: nextBody }));
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось оформить текст"));
    } finally {
      setFormatting(false);
    }
  };

  const revertFormat = () => {
    if (formatBackup == null) return;
    setForm((prev) => ({ ...prev, body: formatBackup }));
    setFormatBackup(null);
  };

  const handleDragOver = (event) => {
    const types = Array.from(event.dataTransfer?.types || []);
    if (!types.includes("Files") && !types.some((type) => String(type).startsWith("image/"))) {
      return;
    }
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setDragOver(true);
  };

  const handleDragLeave = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragOver(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    setDragOver(false);
    const files = [...(event.dataTransfer?.files || [])];
    const image = files.find((file) => file.type.startsWith("image/"));
    if (image) await attachImage(image);
  };

  const handleSave = async (event) => {
    event?.preventDefault?.();
    if (!canEdit) return;
    const title = form.title.trim();
    if (!title) {
      setActionError("Укажите название");
      titleRef.current?.focus();
      return;
    }
    setSaving(true);
    setActionError("");
    try {
      if (docIdRef.current) {
        const updated = await updateRegulation(docIdRef.current, {
          title,
          category: form.category,
          body: form.body,
        });
        setDoc(updated);
        setFormatBackup(null);
        setEditing(false);
        navigate(`/regulations/${updated.id}`, { replace: true });
      } else {
        const created = await createRegulation({
          title,
          category: form.category,
          body: form.body,
        });
        docIdRef.current = created.id;
        setDoc(created);
        setFormatBackup(null);
        setEditing(false);
        navigate(`/regulations/${created.id}`, { replace: true });
      }
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось сохранить"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!docIdRef.current || !canEdit) return;
    setDeleting(true);
    try {
      await deleteRegulation(docIdRef.current);
      navigate(`/regulations?category=${form.category}`, { replace: true });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Не удалось удалить"));
      setConfirmDelete(false);
      setDeleting(false);
    }
  };

  const leaveEditor = () => {
    if (dirty && !window.confirm("Сбросить несохранённые изменения?")) return;
    setFormatBackup(null);
    if (isNew && !doc) navigate(`/regulations?category=${form.category}`);
    else setEditing(false);
  };

  const handleEditorKeyDown = (event) => {
    const meta = event.ctrlKey || event.metaKey;
    if (meta && event.key.toLowerCase() === "s") {
      event.preventDefault();
      handleSave(event);
      return;
    }
    if (event.target !== textareaRef.current) return;
    if (meta && event.key.toLowerCase() === "b") {
      event.preventDefault();
      applyEditorFormat("bold");
      return;
    }
    if (meta && event.key.toLowerCase() === "i") {
      event.preventDefault();
      applyEditorFormat("italic");
      return;
    }
    if (event.key === "Tab") {
      const start = textareaRef.current.selectionStart;
      const end = textareaRef.current.selectionEnd;
      if (!selectionTouchesList(form.body, start, end)) return;
      event.preventDefault();
      applyEditorFormat(event.shiftKey ? "outdent" : "indent");
    }
  };

  const listHref = `/regulations?category=${form.category}`;

  if (isNew && !canEdit) {
    return (
      <div className="page-container regulations-page">
        <p className="reg-empty">Недостаточно прав, чтобы создать инструкцию.</p>
        <Link className="reg-back" to="/regulations">
          К списку
        </Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container regulations-page">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container regulations-page">
        <ErrorRetry message={error} onRetry={load} />
        <Link className="reg-back" to="/regulations">
          К списку
        </Link>
      </div>
    );
  }

  return (
    <div className={`page-container regulations-page${editing ? " is-editing" : ""}`}>
      <div className="page-header staff-header">
        <div className="reg-header-copy">
          <nav className="reg-crumb" aria-label="Навигация">
            <Link className="reg-back" to={listHref}>
              Регламенты
            </Link>
            <span aria-hidden="true">/</span>
            <span>{categoryLabel(form.category)}</span>
          </nav>
          <h2>{editing ? (isNew && !doc ? "Новая инструкция" : "Редактирование") : doc?.title}</h2>
          {!editing && doc && (
            <p className="page-subtitle staff-subtitle">
              {categoryLabel(doc.category)} · {formatDocDate(doc.updated_at)}
              {doc.updated_by_name ? ` · ${doc.updated_by_name}` : ""}
            </p>
          )}
        </div>
        {canEdit && !editing && (
          <div className="staff-header-actions">
            <button type="button" className="btn" onClick={() => setEditing(true)}>
              Изменить инструкцию
            </button>
            <button
              type="button"
              className="btn reg-danger-btn"
              onClick={() => setConfirmDelete(true)}
            >
              Удалить инструкцию
            </button>
          </div>
        )}
      </div>

      {actionError && (
        <div className="login-error" role="alert">
          {actionError}
        </div>
      )}

      {editing ? (
        <form className="reg-editor" onSubmit={handleSave} onKeyDown={handleEditorKeyDown}>
          <div className="reg-editor-meta">
            <label className="login-label" htmlFor="reg-title">
              Название
              <input
                id="reg-title"
                ref={titleRef}
                className="login-input"
                name="title"
                autoComplete="off"
                value={form.title}
                onChange={(event) => setForm({ ...form, title: event.target.value })}
                required
                maxLength={200}
                placeholder="Как закрыть месяц…"
              />
            </label>
            <label className="login-label" htmlFor="reg-category">
              Категория
              <select
                id="reg-category"
                className="login-input"
                name="category"
                autoComplete="off"
                value={form.category}
                onChange={(event) => setForm({ ...form, category: event.target.value })}
              >
                {REGULATION_CATEGORIES.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="reg-editor-toolbar">
            <div className="reg-format-group" role="group" aria-label="Форматирование">
              {FORMAT_ACTIONS.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  className="btn btn-sm"
                  onMouseDown={keepToolbarFocus}
                  onClick={() => applyEditorFormat(action.id)}
                >
                  {action.label}
                </button>
              ))}
            </div>
            <div
              className="reg-pane-tabs"
              role="tablist"
              aria-label="Режим редактора"
            >
              {EDITOR_PANES.map((item) => {
                const selected = pane === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={selected}
                    className={`reg-pane-tab${selected ? " is-active" : ""}`}
                    onClick={() => setPane(item.id)}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              className="btn btn-sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading || formatting}
            >
              {uploading ? "Загрузка…" : "Вставить скриншот"}
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleFormat}
              disabled={formatting || saving || !form.body.trim()}
            >
              {formatting ? "Оформление…" : "Оформить"}
            </button>
            {formatBackup != null && (
              <button type="button" className="btn btn-sm" onClick={revertFormat}>
                Вернуть как было
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              hidden
              aria-label="Загрузить скриншот"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) attachImage(file);
              }}
            />
            <span className="reg-editor-hint">
              Из Word — Ctrl+V. Оформить поправит списки и заголовки. Ctrl+B / Ctrl+I / Ctrl+S
            </span>
          </div>
          <div
            className={`reg-editor-split${dragOver ? " is-dragover" : ""}`}
            data-pane={pane}
          >
            <label
              className="login-label reg-editor-source"
              htmlFor="reg-body"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              Текст
              <textarea
                id="reg-body"
                ref={textareaRef}
                className="login-input reg-textarea"
                name="body"
                autoComplete="off"
                value={form.body}
                onChange={(event) => {
                  setFormatBackup(null);
                  setForm({ ...form, body: event.target.value });
                }}
                onPaste={handlePaste}
                rows={16}
                maxLength={100000}
                placeholder="Наберите текст и выделите фрагмент — кнопки сверху обернут его."
              />
            </label>
            <div className="reg-preview">
              <h3>Как это увидят</h3>
              <MarkdownView source={form.body} />
            </div>
          </div>
          <div className="reg-editor-actions">
            <span className="reg-dirty-flag" aria-live="polite">
              {dirty ? "Не сохранено" : ""}
            </span>
            <button type="submit" className="staff-add-btn" disabled={saving}>
              {saving ? "Сохранение…" : "Сохранить инструкцию"}
            </button>
            <button type="button" className="btn" onClick={leaveEditor}>
              Отмена
            </button>
          </div>
        </form>
      ) : (
        <article className="reg-article">
          <MarkdownView source={doc?.body || ""} />
        </article>
      )}

      {confirmDelete && (
        <div
          className="users-modal-backdrop"
          onClick={() => !deleting && setConfirmDelete(false)}
        >
          <div
            className="users-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="reg-delete-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="reg-delete-title">Удалить инструкцию?</h3>
            <p className="settings-hint">
              «{doc?.title || form.title || "Без названия"}» будет удалена без
              восстановления.
            </p>
            <div className="users-modal-actions">
              <button
                type="button"
                className="btn"
                onClick={() => setConfirmDelete(false)}
                disabled={deleting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="cd-danger-btn reg-modal-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Удаление…" : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
